import { BasicButton, Text } from "@certn/cascadia-react";
import { slugify } from "@certn/utils";
import { css } from "@emotion/css";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import { OrderingCheckCard } from "../../components/ordering-check-card";
import { OrderingUmbrellaSelectOption } from "../../components/ordering-check-card/types";
import { syncMutuallyInclusiveAddOns } from "../order/order-utils";
import { CheckInBundleConflictModal } from "./CheckInBundleConflictModal";
import { PackageEditAddCheckModal } from "./PackageEditAddCheckModal";
import {
  CheckInBundleConflict,
  findBundlesContainingCheck,
  getBundleCheckTypesFromBundle,
} from "./bundle-conflict-utils";
import {
  PackageContext,
  SelectedChecksType,
} from "./package-creation/PackageContext";
import {
  getRequiredChecksFromSelectedChecks,
  getSelectedChecksExclRemovedExclStandalone,
  getUpdatedChecks,
  isCheckStandaloneDisabled,
} from "./package-creation/package-utils";
import { availableCheckToSelectedCheck } from "./package-edit-utils";
import { CheckData } from "./types";

export function PackageEditChecksSection() {
  const { t } = useTranslation();
  const { packageData, setPackage } = useContext(PackageContext);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [checkInBundleConflict, setCheckInBundleConflict] =
    useState<CheckInBundleConflict | null>(null);

  const bundleCheckTypes = [
    ...new Set(
      packageData.bundles.flatMap((bundle) =>
        getBundleCheckTypesFromBundle(bundle)
      )
    ),
  ];

  const handleRemoveCheck = (check: CheckData) => {
    setPackage({
      ...packageData,
      checks: getSelectedChecksExclRemovedExclStandalone(
        packageData.checks,
        check
      ),
    });
  };

  const handleAddCheck = (check: CheckData) => {
    if (packageData.checks.some(({ key }) => key === check.type)) {
      return;
    }

    const conflict = findBundlesContainingCheck(
      { type: check.type, name: check.name },
      packageData.bundles
    );
    if (conflict) {
      setCheckInBundleConflict(conflict);
      return;
    }

    setPackage({
      ...packageData,
      checks: [...packageData.checks, availableCheckToSelectedCheck(check)],
    });
    setIsAddModalOpen(false);
  };

  const handleUpdateCheckAddOns = (
    checkType: string,
    checkArguments: Record<string, unknown>
  ) => {
    const updated = packageData.checks.map((c) =>
      c.key === checkType ? { ...c, check_arguments: checkArguments } : c
    );
    setPackage({
      ...packageData,
      checks: syncMutuallyInclusiveAddOns(updated, checkType, checkArguments),
    });
  };

  const handleUpdateArguments = (
    check_arguments: Record<string, unknown>,
    check: CheckData,
    selectedOptions?: OrderingUmbrellaSelectOption[]
  ) => {
    setPackage({
      ...packageData,
      checks: getUpdatedChecks(
        check_arguments,
        packageData.checks.map((c) => c.checkDetails),
        packageData.checks,
        check,
        selectedOptions
      ),
    });
  };

  return (
    <div className={styles.container}>
      {packageData.checks.length === 0 && (
        <Text style="form/body-secondary">
          {packageData.bundles.length > 0
            ? t(
                "No individual checks yet. Bundle checks are listed on the bundled deal above."
              )
            : t("No checks on this package yet.")}
        </Text>
      )}
      <div className={styles.cards}>
        {packageData.checks.map((selectedCheck: SelectedChecksType) => {
          const check = selectedCheck.checkDetails;
          const isCheckRequired = getRequiredChecksFromSelectedChecks(
            packageData.checks
          ).includes(check.type);
          const isCheckInSelectedBundle = bundleCheckTypes.includes(check.type);

          return (
            <OrderingCheckCard
              isDisabled={
                isCheckInSelectedBundle ||
                isCheckStandaloneDisabled(packageData.checks, check)
              }
              checkOrderingError={
                check.check_has_ordering_error_for_customer_group
              }
              key={check.type}
              type={check.type}
              name={check.name}
              price={selectedCheck.cost}
              currency={check.currency}
              checkChildren={check.children || []}
              turnaroundTime={check.estimated_turnaround_time}
              disclaimersIconList={check.disclaimers_icon_list}
              disclaimers={check.disclaimers}
              description={check.description}
              infoParagraphs={check.info_paragraphs}
              coverage={check.coverage ?? ""}
              keyFeatures={check.key_features}
              howItCanHelpYou={check.how_it_can_help_you}
              selectFieldDescription={check.select_field_descriptions}
              selectFieldEndpoint={check.select_field_endpoint}
              selectOptions={check.select_options}
              onUpdateSelection={(checkArguments, selectedOptions) =>
                handleUpdateArguments(checkArguments, check, selectedOptions)
              }
              handleUpdateCheckAddOns={(args) =>
                handleUpdateCheckAddOns(check.type, args)
              }
              umbrellaChecksSupported={check.children || []}
              umbrellaChecksHeading={check.umbrella_child_checks_heading}
              isSelected
              showRemoveWhenSelected
              onRemove={() => handleRemoveCheck(check)}
              testID={slugify(check.type)}
              isRequiredCheck={isCheckRequired}
              width={480}
              selectedOptions={selectedCheck.selectedOptions}
              selectFieldInformation={check.select_field_information}
              checkArguments={selectedCheck.check_arguments}
              isApplicantPay={packageData.is_applicant_pay}
              addOnOptions={check.add_on_options}
            />
          );
        })}
      </div>
      <BasicButton
        inverse
        size="small"
        leftIcon="add"
        onPress={() => setIsAddModalOpen(true)}
        testID="package-edit-add-check-button"
      >
        {t("Add check")}
      </BasicButton>
      {isAddModalOpen && (
        <PackageEditAddCheckModal
          selectedCheckTypes={[
            ...packageData.checks.map((check) => check.key),
            ...bundleCheckTypes,
          ]}
          isApplicantPay={packageData.is_applicant_pay}
          onAdd={handleAddCheck}
          onClose={() => setIsAddModalOpen(false)}
        />
      )}
      <CheckInBundleConflictModal
        isOpen={checkInBundleConflict !== null}
        conflict={checkInBundleConflict}
        onClose={() => setCheckInBundleConflict(null)}
      />
    </div>
  );
}

const styles = {
  container: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
    alignItems: "flex-start",
  }),
  cards: css({
    display: "flex",
    gap: "var(--space-4)",
    flexWrap: "wrap",
    width: "100%",
  }),
};
